// Apolo IDE — MarkdownBlockEditor (sprint plan-tab-v1, E3).
//
// CodeMirror 6 com markdown syntax pro `text` block. Substitui textarea
// simples do E2 com edição rica: highlight, fold gutter (omitido pra
// economia visual em cards pequenos), line wrapping, focus ring violeta.

import { useEffect, useRef } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, highlightActiveLine } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown } from '@codemirror/lang-markdown';
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching, indentOnInput } from '@codemirror/language';
import { oneDark } from '@codemirror/theme-one-dark';

export default function MarkdownBlockEditor({ value, onChange, placeholder = 'Escreva markdown aqui…' }) {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const valueRef = useRef(value);

  // Initial mount.
  useEffect(() => {
    if (!containerRef.current || viewRef.current) return;
    const startDoc = typeof value === 'string' ? value : '';
    const state = EditorState.create({
      doc: startDoc,
      extensions: [
        history(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        bracketMatching(),
        indentOnInput(),
        highlightActiveLine(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        markdown(),
        oneDark,
        EditorView.lineWrapping,
        EditorView.theme({
          '&': { height: '100%', fontSize: '13px', background: 'transparent' },
          '.cm-scroller': { fontFamily: 'JetBrains Mono, ui-monospace, monospace', lineHeight: '1.5' },
          '.cm-content': { padding: '12px 14px', caretColor: 'var(--apolo-plan-accent)' },
          '.cm-activeLine': { backgroundColor: 'transparent' },
          '.cm-selectionBackground, ::selection': { backgroundColor: 'var(--apolo-plan-accentMuted) !important' },
          '.cm-focused': { outline: 'none' },
          '.cm-editor.cm-focused': { outline: 'none' },
        }),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const next = update.state.doc.toString();
            valueRef.current = next;
            onChange?.(next);
          }
        }),
      ],
    });
    viewRef.current = new EditorView({ state, parent: containerRef.current });
    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value -> editor (only if differs to avoid cursor jump).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (typeof value === 'string' && value !== current) {
      view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
      valueRef.current = value;
    }
  }, [value]);

  return (
    <div className="relative h-full w-full overflow-hidden" data-cm-block>
      {(!value || value.length === 0) ? (
        <span className="pointer-events-none absolute left-3.5 top-3 text-sm text-apolo-textPlaceholder">
          {placeholder}
        </span>
      ) : null}
      <div ref={containerRef} className="h-full w-full" />
    </div>
  );
}
